<template>
  <v-dialog v-model="dialog" max-width="500" persistent>
    <template v-slot:activator="{ props }">
      <slot name="activator" :props="props">
        <v-btn v-bind="props" color="primary" size="large" class="text-none">
          Get Started
        </v-btn>
      </slot>
    </template>

    <v-card rounded="xl">
      <v-card-title class="d-flex align-center pa-4">
        <div class="text-h6 font-weight-bold">
          {{ mode === 'create' ? 'Create a Household' : 'Join a Household' }}
        </div>
        <v-spacer />
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-btn-toggle v-model="mode" mandatory divided density="comfortable" class="w-100 mb-4">
          <v-btn value="create" class="text-none">Create New</v-btn>
          <v-btn value="join" class="text-none">Join Existing</v-btn>
        </v-btn-toggle>

        <v-form ref="form" @submit.prevent="submit">
          <template v-if="mode === 'create'">
            <v-text-field v-model="householdName" label="Household Name" placeholder="e.g. Smith Family, My Apartment"
              variant="outlined" density="comfortable" :rules="nameRules" autofocus />
            <p class="text-body-2 text-medium-emphasis mt-2">
              A household is a shared kitchen space for the people you cook with. Creating one
              automatically adds a Kitchen pantry and a meal plan. You can invite others to join later.
            </p>
          </template>

          <template v-else>
            <v-text-field v-model="inviteCode" label="Invite Code" placeholder="Enter the 8-character code"
              variant="outlined" density="comfortable" :rules="codeRules" autofocus @input="formatInviteCode" />
            <p class="text-body-2 text-medium-emphasis mt-2">
              Ask the household owner for their invite code or link.
            </p>
          </template>

          <v-alert v-if="error" type="error" variant="tonal" class="mt-3">
            {{ error }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn variant="text" class="text-none" @click="close">Cancel</v-btn>
        <v-spacer />
        <v-btn color="primary" class="text-none" :loading="loading" @click="submit">
          {{ mode === 'create' ? 'Create Household' : 'Join Household' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useHouseholdStore } from '@/stores/householdStore'
import { rules } from '@/utils/validation'

const emit = defineEmits<{
  success: []
}>()

const householdStore = useHouseholdStore()

const dialog = ref(false)
const mode = ref<'create' | 'join'>('create')
const householdName = ref('')
const inviteCode = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

type VFormRef = { validate: () => Promise<{ valid: boolean }>; reset: () => void }
const form = ref<VFormRef | null>(null)

const nameRules = [rules.required('Give your household a name')]
const codeRules = [
  rules.required('Enter an invite code'),
  rules.minLength(8, 'Invite code must be 8 characters')
]

const formatInviteCode = (): void => {
  inviteCode.value = inviteCode.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
}

const close = (): void => {
  dialog.value = false
  error.value = null
  householdName.value = ''
  inviteCode.value = ''
  mode.value = 'create'
  form.value?.reset()
}

const submit = async (): Promise<void> => {
  error.value = null

  const validation = form.value ? await form.value.validate() : { valid: true }
  if (!validation.valid) return

  loading.value = true

  try {
    if (mode.value === 'create') {
      const id = await householdStore.createHousehold(householdName.value.trim())
      if (id) {
        emit('success')
        close()
      } else {
        error.value = householdStore.error || 'Failed to create household'
      }
    } else {
      const success = await householdStore.joinHouseholdByInviteCode(inviteCode.value)
      if (success) {
        emit('success')
        close()
      } else {
        error.value = householdStore.error || 'Failed to join household'
      }
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'An error occurred'
  } finally {
    loading.value = false
  }
}
</script>
